/**
 * Network Overview Component
 * Combined view of CBO Network and Network Visualization
 * Trust Marketplace - Network Management System
 */

import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import CBONetworkView from './CBONetworkView';
import NetworkVisualization from './NetworkVisualization';
import { Share2, Network } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
interface NetworkOverviewProps {
  cboId?: number;
  sponsorId?: number;
}

const NetworkOverview: React.FC<NetworkOverviewProps> = ({ cboId, sponsorId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div>
            {!tabbed && <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('network.network_overview', 'Network Overview')}</h1>}
            <p className="text-gray-600 mt-1">
              {t('network.view_your_cbo_network_connections', 'View your CBO network connections and program participation')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs defaultValue="network" className="w-full">
        <div className="bg-white dark:bg-gray-800 border-b-2 border-gray-100 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-6">
            <TabsList className="flex flex-row items-center gap-0 bg-transparent border-0 p-0 h-14 w-full justify-start">
              <TabsTrigger
                value="network"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                {t('network.cbo_network', 'CBO Network')}
              </TabsTrigger>
              <TabsTrigger
                value="visualization"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <Network className="w-4 h-4" />
                {t('network.network_visualization', 'Network Visualization')}
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <TabsContent value="network" className="mt-0">
            <CBONetworkView cboId={cboId} />
          </TabsContent>

          <TabsContent value="visualization" className="mt-0">
            <NetworkVisualization sponsorId={sponsorId} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default NetworkOverview;
